import { IconBuilding, IconUser } from '@iwoca/icons';
import { IconMoneyPound1 } from '@iwoca/icons/brand';
import { Tooltip } from '@iwoca/orion';
import clsx from 'clsx';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { useGetCommissionOverview } from 'lendingApi/lendingApiHooks';
import Skeleton from 'react-loading-skeleton';
import {
  getCommissionDateRange,
  getCommissionMonthLabel,
} from './CommissionOverview.helpers';
import styles from './CommissionOverview.module.css';

type TProps = {
  introducerId: string;
  isMasterBroker: boolean;
  variant?: 'desktop' | 'mobile';
};

const ERROR_TOOLTIP =
  "Sorry, couldn't load commission right now. Refresh to try again.";
const ERROR_VALUE = '£–';

export const CommissionOverview = ({
  introducerId,
  isMasterBroker,
  variant = 'desktop',
}: TProps) => {
  const { data, isLoading, isError } = useGetCommissionOverview(introducerId);
  const { formatCurrency } = useNumberFormat();

  const monthLabel = getCommissionMonthLabel();
  const dateRange = getCommissionDateRange();

  const hasError = isError || (!isLoading && !data);

  const showBrokerageTotal =
    isLoading || hasError
      ? isMasterBroker
      : isMasterBroker &&
        typeof data?.data.account_this_month_commission_total === 'string';

  const individualBrokerTooltip = hasError
    ? ERROR_TOOLTIP
    : `Commission paid to you between ${dateRange}`;
  const brokerageTooltip = hasError
    ? ERROR_TOOLTIP
    : `Commission paid to your brokerage between ${dateRange}`;

  const renderFigureValue = (formattedValue: string) => {
    if (isLoading) {
      return (
        <Skeleton
          borderRadius={10}
          width={52}
          height={14}
          baseColor="var(--color-secondary-90)"
        />
      );
    }
    return hasError ? ERROR_VALUE : formattedValue;
  };

  const individualBrokerFormatted = data
    ? formatCurrency(data.data.this_month_commission_total)
    : ERROR_VALUE;
  const brokerageFormatted =
    data?.data.account_this_month_commission_total !== null &&
    data?.data.account_this_month_commission_total !== undefined
      ? formatCurrency(data.data.account_this_month_commission_total)
      : ERROR_VALUE;

  const pill = (
    <span
      className={clsx(styles.pill, { [styles.mobile]: variant === 'mobile' })}
      data-testid="commission-overview"
    >
      <IconMoneyPound1 className={styles.leadingIcon} aria-hidden />
      <span className={styles.label}>{monthLabel}</span>

      {showBrokerageTotal ? (
        <span className={styles.figures}>
          <Tooltip content={individualBrokerTooltip} placement="bottom">
            <span className={styles.figure} data-testid="commission-individual">
              <IconUser className={styles.figureIcon} aria-hidden />
              {renderFigureValue(individualBrokerFormatted)}
            </span>
          </Tooltip>
          <span className={styles.divider} aria-hidden />
          <Tooltip content={brokerageTooltip} placement="bottom">
            <span className={styles.figure} data-testid="commission-brokerage">
              <IconBuilding className={styles.figureIcon} aria-hidden />
              {renderFigureValue(brokerageFormatted)}
            </span>
          </Tooltip>
        </span>
      ) : (
        <span className={styles.figure} data-testid="commission-individual">
          {renderFigureValue(individualBrokerFormatted)}
        </span>
      )}
    </span>
  );

  if (showBrokerageTotal) {
    return pill;
  }

  const tooltip = (
    <Tooltip content={individualBrokerTooltip} placement="bottom">
      {pill}
    </Tooltip>
  );

  if (variant === 'mobile') {
    return <div className={styles.mobileTooltip}>{tooltip}</div>;
  }

  return tooltip;
};
